import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronLeft, ChevronRight, Plus, ExternalLink, Check } from 'lucide-react';
import { startOfISOWeek, addDays, getISOWeek, getYear, format, isSameDay } from 'date-fns';
import { de } from 'date-fns/locale';
import { api } from '../api/client';
import { useApp } from '../contexts/AppContext';
import { toast, StatusBadge, MicButton, Checkbox } from './UI';
import TaskModal from './TaskModal';

function getWeekId(date) {
  return `${getYear(date)}-${String(getISOWeek(date)).padStart(2, '0')}`;
}

export default function WeekView({ onNavigateToDay }) {
  const { tasks, refreshTasks, isDone } = useApp();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [retro, setRetro] = useState('');
  const [retroStatus, setRetroStatus] = useState('idle'); // idle | saving | saved
  const [openTaskId, setOpenTaskId] = useState(null);
  const [newGoalTitle, setNewGoalTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const [weekMarks, setWeekMarks] = useState(new Set());

  const monday = useMemo(() => startOfISOWeek(currentDate), [currentDate]);
  const mondayStr = format(monday, 'yyyy-MM-dd');
  const weekId = useMemo(() => getWeekId(monday), [monday]);

  const weekGoals = useMemo(
    () => tasks.filter(t => t.scope === 'week' && t.period_date?.split('T')[0] === mondayStr),
    [tasks, mondayStr]
  );

  const editTask = openTaskId ? tasks.find(t => t.id === openTaskId) : null;

  // Load retro for current week
  useEffect(() => {
    let cancelled = false;
    api.getWeekPlan(weekId).then(plan => {
      if (cancelled) return;
      const r = plan?.retro || '';
      setRetro(r);
      lastSavedRetro.current = r;
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [weekId]);

  // Load per-week marks (strikethrough for week goals)
  useEffect(() => {
    let cancelled = false;
    api.getMarks({ period_type: 'week', period_date: mondayStr }).then(rows => {
      if (cancelled) return;
      setWeekMarks(new Set((rows || []).map(r => r.task_id)));
    }).catch(() => { if (!cancelled) setWeekMarks(new Set()); });
    return () => { cancelled = true; };
  }, [mondayStr]);

  const toggleWeekMark = async (taskId) => {
    const isMarked = weekMarks.has(taskId);
    setWeekMarks(prev => {
      const next = new Set(prev);
      if (isMarked) next.delete(taskId); else next.add(taskId);
      return next;
    });
    try {
      if (isMarked) await api.unsetMark({ task_id: taskId, period_type: 'week', period_date: mondayStr });
      else await api.setMark({ task_id: taskId, period_type: 'week', period_date: mondayStr });
    } catch (err) {
      setWeekMarks(prev => {
        const next = new Set(prev);
        if (isMarked) next.add(taskId); else next.delete(taskId);
        return next;
      });
      toast(err.message || 'Mark konnte nicht gespeichert werden', 'error');
    }
  };

  // Debounced retro save
  const lastSavedRetro = useRef('');
  useEffect(() => {
    if (retro === lastSavedRetro.current) return;
    setRetroStatus('saving');
    const timer = setTimeout(async () => {
      try {
        await api.saveWeekPlan(weekId, { retro });
        lastSavedRetro.current = retro;
        setRetroStatus('saved');
        setTimeout(() => setRetroStatus(s => s === 'saved' ? 'idle' : s), 1500);
      } catch (err) {
        setRetroStatus('idle');
        toast(err.message || 'Rückblick konnte nicht gespeichert werden', 'error');
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [retro, weekId]);

  const addGoal = async () => {
    const title = newGoalTitle.trim();
    if (!title) return;
    setAdding(true);
    setNewGoalTitle('');
    try {
      await api.createTask({ title, scope: 'week', period_date: mondayStr, status: 'Offen' });
      refreshTasks();
    } catch (err) {
      toast(err.message || 'Wochenziel konnte nicht angelegt werden', 'error');
    } finally {
      setAdding(false);
    }
  };

  const prevWeek = () => setCurrentDate(d => addDays(d, -7));
  const nextWeek = () => setCurrentDate(d => addDays(d, 7));
  const thisWeek = () => setCurrentDate(new Date());

  const days = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  const today = new Date();

  return (
    <div style={{ padding: '24px', maxWidth: 800, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, fontSize: 28 }}>
            KW {getISOWeek(monday)} · {getYear(monday)}
          </h1>
          <div style={{ color: 'var(--text-3)', fontSize: 13 }}>
            {format(monday, 'dd. MMM', { locale: de })} – {format(addDays(monday, 6), 'dd. MMM yyyy', { locale: de })}
          </div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={prevWeek} title="Vorige Woche"><ChevronLeft size={14} /></button>
        <button className="btn btn-ghost btn-sm" onClick={thisWeek}>Diese Woche</button>
        <button className="btn btn-ghost btn-sm" onClick={nextWeek} title="Nächste Woche"><ChevronRight size={14} /></button>
      </div>

      {/* Wochenziele */}
      <div className="card">
        <label style={{ fontFamily: 'var(--font-serif)', fontSize: 16, fontWeight: 400, color: 'var(--text)', marginBottom: 10, display: 'block' }}>
          ⭐ Wochenziele
          <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 400, marginLeft: 8 }}>(synchron mit Planung)</span>
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
          {weekGoals.length === 0 && (
            <div style={{ color: 'var(--text-3)', fontSize: 13 }}>Noch keine Ziele für diese Woche</div>
          )}
          {weekGoals.map(g => {
            const isMarked = weekMarks.has(g.id);
            const statusDone = isDone(g);
            const struck = isMarked || statusDone;
            return (
              <div key={g.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                  background: 'var(--bg-2)', borderRadius: 'var(--radius)',
                  opacity: struck ? 0.6 : 1,
                }}
              >
                <span onClick={(e) => { e.stopPropagation(); toggleWeekMark(g.id); }}
                  title="Für diese Woche abhaken (Task-Status bleibt)">
                  <Checkbox checked={isMarked} onChange={() => {}} />
                </span>
                <span
                  onClick={() => setOpenTaskId(g.id)}
                  style={{
                    flex: 1, fontSize: 13, cursor: 'pointer',
                    color: struck ? 'var(--text-3)' : 'var(--text)',
                    textDecoration: struck ? 'line-through' : 'none',
                  }}
                  title="Task-Detail öffnen"
                >
                  {g.title}
                </span>
                <StatusBadge status={g.status} />
                <ExternalLink size={11} color="var(--text-3)" />
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <input value={newGoalTitle}
            onChange={e => setNewGoalTitle(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addGoal()}
            placeholder="Neues Wochenziel..." style={{ flex: 1 }} />
          <MicButton onTranscript={(t) => setNewGoalTitle(prev => prev ? `${prev} ${t}` : t)} />
          <button className="btn btn-ghost btn-sm" onClick={addGoal} disabled={adding || !newGoalTitle.trim()}>
            <Plus size={12} /> Hinzufügen
          </button>
        </div>
      </div>

      {/* Tage der Woche */}
      <div className="card">
        <label style={{ fontFamily: 'var(--font-serif)', fontSize: 16, fontWeight: 400, color: 'var(--text)', marginBottom: 10, display: 'block' }}>
          📅 Tage der Woche
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
          {days.map(d => {
            const dStr = format(d, 'yyyy-MM-dd');
            const isToday = isSameDay(d, today);
            return (
              <button key={dStr}
                onClick={() => onNavigateToDay && onNavigateToDay(dStr)}
                style={{
                  background: isToday ? 'var(--accent-dim)' : 'var(--bg-2)',
                  border: `1px solid ${isToday ? 'var(--accent)' : 'var(--border)'}`,
                  borderRadius: 'var(--radius)',
                  padding: '10px 6px', cursor: 'pointer', textAlign: 'center',
                  color: isToday ? 'var(--accent)' : 'var(--text)',
                }}
              >
                <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{format(d, 'EE', { locale: de })}</div>
                <div style={{ fontSize: 16, fontWeight: 500 }}>{format(d, 'dd')}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Retrospektive */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <label style={{ fontFamily: 'var(--font-serif)', fontSize: 16, fontWeight: 400, color: 'var(--text)', flex: 1 }}>
            📝 Wochenrückblick
          </label>
          {retroStatus === 'saving' && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Speichert…</span>}
          {retroStatus === 'saved' && <span style={{ fontSize: 11, color: 'var(--green)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Check size={11} /> Gespeichert</span>}
        </div>
        <textarea value={retro} onChange={e => setRetro(e.target.value)}
          rows={5} placeholder="Was lief gut? Was nicht? Was nehme ich in die nächste Woche mit?" />
      </div>

      {editTask && (
        <TaskModal
          task={editTask}
          onClose={() => setOpenTaskId(null)}
          onSaved={refreshTasks}
          onNavigate={(t) => setOpenTaskId(t?.id || null)}
        />
      )}
    </div>
  );
}
